import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { getAdminUsers, checkSuperAdmin } from '../../lib/adminQueries'
import { supabase } from '../../lib/supabase'
import './AdminUsersManager.css'

function AdminUsersManager() {
  const [admins, setAdmins] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [newEmail, setNewEmail] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const [successMessage, setSuccessMessage] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const { user } = useAuth()

  const fetchAdmins = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data, error } = await getAdminUsers()
      if (error) throw error
      setAdmins(data || [])
    } catch (err) {
      console.error('Error fetching admins:', err)
      setError(err.message || 'Failed to load admin users')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAdmins()

    const checkUserSuperAdminStatus = async () => {
      if (user?.email) {
        const superAdmin = await checkSuperAdmin(user.email)
        setIsSuperAdmin(superAdmin)
      }
    }
    checkUserSuperAdminStatus()
  }, [user, fetchAdmins])

  const handleAddAdmin = async (e) => {
    e.preventDefault()
    if (!newEmail.trim()) return

    setIsAdding(true)
    setError(null)
    setSuccessMessage(null)

    try {
      // Get the current session to ensure we have a valid token
      const { data: sessionData } = await supabase.auth.getSession()
      console.log('Current session:', sessionData?.session ? 'Active' : 'None')
      console.log('Session user:', sessionData?.session?.user?.email)

      if (!sessionData?.session) {
        throw new Error('No active session. Please log in again.')
      }

      console.log('Invoking create-admin-user function...')
      const { data, error } = await supabase.functions.invoke('create-admin-user', {
        body: { email: newEmail },
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`
        }
      })
      console.log('Function response:', { data, error })

      // Handle errors - check data first since function returns error messages there
      if (data?.error) {
        console.error('Function returned error:', data.error)
        throw new Error(data.error)
      }

      // Handle other function errors
      if (error) {
        console.error('Function invocation error:', error)
        // Try to get actual error from response context
        let errorMessage = 'Failed to add admin user'
        try {
          if (error.context?.json) {
            const json = await error.context.json()
            errorMessage = json.error || errorMessage
          }
        } catch (e) {
          console.error('Could not parse error response:', e)
        }
        throw new Error(errorMessage)
      }

      setNewEmail('')
      setSuccessMessage(data?.message || 'Invite sent successfully!')
      await fetchAdmins()
    } catch (err) {
      console.error('Error adding admin:', err)
      setError(err.message || 'Failed to add admin user')
    } finally {
      setIsAdding(false)
    }
  }

  const handleRemoveAdmin = async (id, email) => {
    // Prevent removing yourself
    if (email === user?.email) {
      setError("You cannot remove yourself as an admin")
      return
    }

    try {
      // Get current session for auth header
      const { data: sessionData } = await supabase.auth.getSession()
      if (!sessionData?.session) {
        throw new Error('No active session')
      }

      const { data, error } = await supabase.functions.invoke('remove-admin-user', {
        body: { email },
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`
        }
      })

      if (data?.error) {
        throw new Error(data.error)
      }
      if (error) {
        throw new Error(error.message || 'Failed to remove admin')
      }

      setDeleteConfirm(null)
      setSuccessMessage('Admin removed successfully')
      await fetchAdmins()
    } catch (err) {
      console.error('Error removing admin:', err)
      setError(err.message || 'Failed to remove admin user')
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="admin-users-manager">
      <div className="manager-header">
        <h2>Manage Admin Users</h2>
        <p>Add or remove users who can access the admin dashboard</p>
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {successMessage && (
        <div className="success-banner">
          <p>{successMessage}</p>
          <button onClick={() => setSuccessMessage(null)}>Dismiss</button>
        </div>
      )}

      {isSuperAdmin ? (
        <form onSubmit={handleAddAdmin} className="add-admin-form">
          <div className="form-row">
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter email address"
              required
              disabled={isAdding}
            />
            <button type="submit" disabled={isAdding || !newEmail.trim()}>
              {isAdding ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
          <p className="form-help">
            They will receive an email to set their own password.
          </p>
        </form>
      ) : (
        <div className="info-banner">
          <p>Only super admins can add or remove admin users.</p>
        </div>
      )}

      <div className="admins-list">
        <h3>Current Admins ({admins.length})</h3>

        {loading ? (
          <div className="loading-state">Loading admin users...</div>
        ) : admins.length === 0 ? (
          <div className="empty-state">No admin users found. Add one above.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Added</th>
                <th>Added By</th>
                {isSuperAdmin && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <tr key={admin.id}>
                  <td className="email-cell">
                    {admin.email}
                    {admin.email === user?.email && (
                      <span className="you-badge">You</span>
                    )}
                  </td>
                  <td>
                    {admin.is_super_admin ? (
                      <span className="super-badge">Super Admin</span>
                    ) : (
                      <span className="admin-badge">Admin</span>
                    )}
                  </td>
                  <td>{formatDate(admin.created_at)}</td>
                  <td>{admin.added_by || '-'}</td>
                  {isSuperAdmin && (
                    <td>
                      {admin.is_super_admin ? (
                        <span className="protected-text">Protected</span>
                      ) : deleteConfirm === admin.id ? (
                        <div className="confirm-actions">
                          <button
                            className="confirm-delete"
                            onClick={() => handleRemoveAdmin(admin.id, admin.email)}
                          >
                            Confirm
                          </button>
                          <button
                            className="cancel-delete"
                            onClick={() => setDeleteConfirm(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="remove-button"
                          onClick={() => setDeleteConfirm(admin.id)}
                          disabled={admin.email === user?.email}
                          title={admin.email === user?.email ? "You cannot remove yourself" : "Remove admin"}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default AdminUsersManager
